import { isHttpClientErrorResponse } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type { IrremovableFieldsConfig } from '@atlassian/jira-jql-builder-basic/src/utils/irremovable-fields/index.tsx';
import { getProjectKeyId } from '@atlassian/jira-router-resources-utils/src/utils/get-project-key-id/index.tsx';
import { createResource, useResource, type RouterContext } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import {
	DEFAULT_CALENDAR_FILTER_FIELDS,
	REQUEST_TYPE_FILTER,
	RESOURCE_TYPE_FILTER_FIELD,
} from '../../common/constants.tsx';
import { QUERY } from './gql.tsx';
import type { FilterFieldResponse } from './types.tsx';
import { reportError, transformAndReport } from './utils.tsx';

const fetchFilterFields = async (
	cloudId: string,
	jqlContext: string,
): Promise<IrremovableFieldsConfig> => {
	try {
		const response = await performPostRequest<FilterFieldResponse>('/gateway/api/graphql', {
			body: JSON.stringify({
				query: QUERY,
				variables: {
					cloudId,
					contextFieldsFilter: {
						searchString: REQUEST_TYPE_FILTER,
					},
					jqlContext,
				},
			}),
			headers: {
				'content-type': 'application/json',
				'X-ExperimentalApi': 'JiraJqlBuilder',
			},
		});

		return transformAndReport(response);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (!isHttpClientErrorResponse(error)) {
			reportError(error);
		}

		return DEFAULT_CALENDAR_FILTER_FIELDS;
	}
};

const filterFieldsResource = createResource({
	type: RESOURCE_TYPE_FILTER_FIELD,
	getKey: ({ match, query }: RouterContext) => {
		if (fg('jsm_views_inside_queues_-_main_flag')) {
			const { projectKey, projectId } = getProjectKeyId(match, query);
			const projectIdOrKey = projectKey || projectId;
			return RESOURCE_TYPE_FILTER_FIELD + projectIdOrKey;
		}
		return RESOURCE_TYPE_FILTER_FIELD;
	},
	getData: ({ match, query }, { tenantContext: { cloudId } }) => {
		const { projectKey, projectId } = getProjectKeyId(match, query);
		const projectIdOrKey = projectKey || projectId;
		const jqlContext = `project = '${projectIdOrKey}'`;

		return fetchFilterFields(cloudId, jqlContext);
	},
	maxAge: Infinity,
});

export const getCalendarFilterFieldsResource = () => filterFieldsResource;
export const useCalendarFilterResource = () => useResource(filterFieldsResource);
